import React, { FC, SyntheticEvent, useCallback } from "react";
import { Box, Divider, Table, TableCell, TableHead, TableRow } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import { UseMutateFunction } from "@tanstack/react-query";
import { ProjectResponse } from "@api/projects/mutations";
import { CreateProjectForm } from "@pages/create-project/create-project";
import { TabsComponent } from "@components/ui-kit/tabs";
import { TabPanel } from "@components/ui-kit/tab-panel";
import { OfferField } from "@components/ui-kit/offer-field";
import { ElectricityDemandGraph } from "@components/create-project/graphs/electricity-demand-graph";
import { TechnicalCalculationsTable } from "@components/create-project/table/technical-calculations-table";
import { PriceInformationTable } from "@components/create-project/table/price-information-table";
import { OfferGcTable } from "@components/create-project/table/offer-gc-table";
import { useTabs } from "@hooks/use-tabs";
import debounce from "@utils/utils";
import { OfferGcGraph } from "@components/create-project/graphs/offer-gc-graph";
import { useTranslation } from "react-i18next";
import { FinancialCalculationsTable } from "@components/create-project/table/financial-calculations-table";
import { FinancialsTable } from "@components/create-project/table/financials-table";
import { FinancialsGraph } from "@components/create-project/graphs/financials-graph";
import { FinancialCalculationGraph } from "@components/create-project/graphs/financial-calculations-graph";
import { OfferTechField } from "@components/ui-kit/offer-tech-field";
import { RelativeComparisonGraph } from "@components/create-project/graphs/reletive-comparison-graph";
import { ApmwhGraph } from "@components/create-project/graphs/apmwg-graph";
import { LifeCycleCostGraph } from "@components/create-project/graphs/lifecycle-cost-graph";

interface FinalStepProps {
  offer: ProjectResponse;
  calculateOffer: UseMutateFunction<any, Error, CreateProjectForm, unknown>;
}

export const FinalStep: FC<FinalStepProps> = ({calculateOffer, offer}) => {
  const { t } = useTranslation();
  const {value, handleChange} = useTabs(0);
  const {values, setValues} = useFormikContext<CreateProjectForm>();
  const tabLabels = [t("stepper.offerTabs.1"), t("stepper.offerTabs.2"), t("stepper.offerTabs.3")];

  const handleTabChange = (event: SyntheticEvent, index: number) => { // TODO: Remove this when indexation tab is implemented
    if (index === 2) return;

    handleChange(event, index);
  };

  const debouncedCalculateOffer = useCallback(
    debounce((updatedValues: CreateProjectForm) => {
      calculateOffer(updatedValues);
    }, 600),
    [calculateOffer]
  );

  const handleOfferInputChange = useCallback((name: string, value: number) => {
    const updatedValues = {...values, [name]: value};

    // Update Formik values
    setValues(updatedValues);

    // Call the API to calculate offer with the updated input after debounce
    debouncedCalculateOffer(updatedValues);
  }, [values, setValues, debouncedCalculateOffer]);

  const handleThermalRenovationChange = useCallback(() => {
    const updatedValue = !values["with_thermal_renovation"];
    const updatedValues = {...values, with_thermal_renovation: updatedValue};

    setValues(updatedValues);
    debouncedCalculateOffer(updatedValues);
  }, [values, setValues, debouncedCalculateOffer]);

  const renderInfo = () => (
    <Grid container columnSpacing={2}>
      <Grid size={4}>
        <p className="text-black-800 font-medium text-sm mb-2">{t("project.table.firstColumnTitle")}</p>
        <OfferField name={t("project.projectName")} value={offer?.outputs.Building["Project Name"]}/>
        <OfferField name={t("project.customerName")} value={offer?.customer_name}/>
        <OfferField name={t("project.heatedArea")} value={offer?.outputs.Building["Heated Area"]} units="m2"/>
        <OfferField name={t("project.table.infrastructure")} value={offer?.outputs.Building["Infrastructure"]}/>
        <OfferField name={t("project.exteriorArea")} value={offer?.outputs.Building["Exterior Area"]} units="m2"/>
        <OfferField name={t("project.table.pvAreaTotal")} value={offer?.outputs.Building["PV Area Total"]} units="m2"/>
        <OfferField name={t("project.table.pvPerformance")} value={offer?.outputs.Building["PV performance"]}
                    units="kWp" noBorder/>
        {/* TODO: This will be implemented later */}
        {/*<OfferField*/}
        {/*  name={t('project.thermalRenovation')}*/}
        {/*  value={*/}
        {/*    <FormCheckbox*/}
        {/*      className="pr-0"*/}
        {/*      checked={values.with_thermal_renovation}*/}
        {/*      onChange={handleThermalRenovationChange}*/}
        {/*    />*/}
        {/*  }*/}
        {/*  noBorder*/}
        {/*/>*/}
      </Grid>
      <Grid size={4} sx={{pl: 2, borderLeft: "1px solid #18181B1F"}}>
        <p className="text-black-800 font-medium text-sm mb-2">{t("project.table.secondColumnTitle")}</p>
        <OfferTechField name={t("project.table.heating")} value1={offer?.outputs.Tech.HWB}
                        value2={offer?.outputs.Tech["HWB spec"]} units1="MWh" units2="kWh/m2*a"/>
        <OfferTechField name={t("project.table.warmWater")} value1={offer?.outputs.Tech.WWB}
                        value2={offer?.outputs.Tech["WWB spec"]} units1="MWh" units2="kWh/m2*a"/>
        <OfferTechField name={t("project.table.tempering")} value1={offer?.outputs.Tech.TEMPB}
                        value2={offer?.outputs.Tech["TEMPB spec"]} units1="MWh" units2="kWh/m2*a" underline/>
        <OfferTechField name={t("project.table.total")} value1={offer?.outputs.Tech.Total} units1="MWh" noBorder/>
      </Grid>
      <Grid size={4} sx={{pl: 2, borderLeft: "1px solid #18181B1F"}}>
        <p className="text-black-800 font-medium text-sm mb-2">{t("project.table.thirdColumnTitle")}</p>
        <OfferField name={t("project.table.effectiveInterestRate")}
                    value={offer?.outputs["Financials (General)"]["Effective Interest Rate"]} units="%"/>
        <OfferField name={t("project.table.indexation")} value={offer?.outputs["Financials (General)"]["Indexation"]}
                    units="%"/>
        <OfferField name={t("project.table.subsidyRiskTakenBy")}
                    value={offer?.outputs["Financials (General)"]["Subsidy risk taken by"]}/>
        <OfferField name={t("project.table.billingType")}
                    value={offer?.outputs["Financials (General)"]["Billing Type"]}/>
        <OfferField name={t("project.table.externalBillingSupport")}
                    value={offer?.outputs["Financials (General)"]["External Billing Support"]}/>
        <OfferField name={t("project.table.externalOperationsMgmt")}
                    value={offer?.outputs["Financials (General)"]["External Operations Mgmt"]} noBorder/>
      </Grid>
    </Grid>
  );

  const renderTableHead = () => (
    <TableHead>
      <TableRow>
        <TableCell sx={{borderBottom: "none"}}>{t("project.table.title")}</TableCell>
        <TableCell sx={{borderBottom: "none"}}>
          <p className="font-medium text-black-800 text-center">{t("project.table.case1.heading")}</p>
          <p className="text-metallic-800 text-center">{t("project.table.case1.subHeading")}</p>
        </TableCell>
        <TableCell sx={{borderBottom: "none"}}>
          <p className="font-medium text-black-800 text-center">{t("project.table.case2.heading")}</p>
          <p className="text-metallic-800 text-center">{t("project.table.case2.subHeading")}</p>
        </TableCell>
        <TableCell sx={{borderBottom: "none"}}>
          <p className="font-medium text-black-800 text-center">{t("project.table.case3.heading")}</p>
          <p className="text-metallic-800 text-center">{t("project.table.case3.subHeading")}</p>
        </TableCell>
      </TableRow>
    </TableHead>
  );

  return (
    <Box display="flex" flexDirection="column" width="100%">
      <TabsComponent value={value} handleChange={handleTabChange} tabLabels={tabLabels}/>

      {/* First tab */}
      <TabPanel value={value} index={0}>
        {renderInfo()}
        <Divider variant="fullWidth" sx={{marginTop: "30px", marginBottom: "40px"}}/>
        <Grid container spacing={0} direction="column">
          {/* Technical calculations table-graph pair */}
          <Grid container spacing={0}>
            <Grid size={6}>
              <Table>
                {renderTableHead()}
                <TechnicalCalculationsTable offer={offer}/>
              </Table>
            </Grid>
            <Grid size={6} display="flex" justifyContent="center">
              <ElectricityDemandGraph offer={offer}/>
            </Grid>
          </Grid>

          {/* Financial calculations table-graph pair */}
          <Box sx={{mt: 4}}>
            <Grid container spacing={0}>
              <Grid size={6}>
                <Table>
                  <FinancialCalculationsTable offer={offer}/>
                </Table>
              </Grid>
              <Grid size={6} display="flex" justifyContent="center">
                <FinancialCalculationGraph offer={offer}/>
              </Grid>
            </Grid>
          </Box>

          {/* Financials table-graph pair */}
          <Box sx={{mt: 4}}>
            <Grid container spacing={0}>
              <Grid size={6}>
                <Table>
                  <FinancialsTable offer={offer}/>
                </Table>
              </Grid>
              <Grid size={6} display="flex" justifyContent="center">
                <FinancialsGraph offer={offer}/>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </TabPanel>

      {/* Second tab */}
      <TabPanel value={value} index={1}>
        {renderInfo()}

        <Divider variant="fullWidth" sx={{marginTop: "30px", marginBottom: "40px"}}/>

        <Grid container spacing={0} direction="column">
          <Grid container spacing={0}>
            <Grid size={6}>
              <Table>
                {renderTableHead()}
                <PriceInformationTable offer={offer}/>
              </Table>
            </Grid>
            <Grid size={6} display="flex" flexDirection="column" justifyContent="center">
              <RelativeComparisonGraph offer={offer}/>
              <ApmwhGraph offer={offer}/>
            </Grid>
            <Grid size={12}>
              <div className="w-full bg-black text-white pl-2 mt-4">
                {t("project.table.priceInformation.title")}
              </div>
              <LifeCycleCostGraph offer={offer}/>
            </Grid>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Third tab */}
      <TabPanel value={value} index={2}>
        {renderInfo()}

        <Divider variant="fullWidth" sx={{marginTop: "30px", marginBottom: "40px"}}/>

        <Grid container spacing={2} justifyContent="center" alignItems="center">
          <Grid
            size={6}
            sx={{
              "@media print": {
                width: "100% !important",
              },
            }}
          >
            <Table>
              {renderTableHead()}
              <OfferGcTable offer={offer}/>
            </Table>
          </Grid>
          <Grid size={6} display="flex" justifyContent="center" alignItems="center">
            <OfferGcGraph offer={offer}/>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};
